import React from "react";
import { useLocation } from "react-router-dom";

import { blogPosts } from "data/blogPosts";

const SITE_URL = "https://quivio.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const DEFAULT_TITLE = "Quivio | Software Design, Engineering & On-Prem AI";
const DEFAULT_DESCRIPTION =
  "Quivio is a Canadian software design and engineering studio building custom applications, dependable platforms, and Quivio Enclave KMS for private on-premises AI knowledge.";

const pageMetadata = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/services": {
    title: "Software Services | Quivio",
    description:
      "Quivio designs and builds dependable custom software, product platforms, integrations, and digital workflows for teams with complex operations.",
  },
  "/quivio-enclave": {
    title: "Quivio Enclave KMS | Private On-Prem AI Knowledge System",
    description:
      "Quivio Enclave KMS is a private on-prem AI knowledge system using local AI models, so organizations can avoid sending sensitive information to third-party AI providers.",
  },
  "/works": {
    title: "Work | Quivio",
    description:
      "Explore selected Quivio software design and engineering work across applications, platforms, workflows, and digital product systems.",
  },
  "/blog": {
    title: "Insights | Quivio",
    description:
      "Read Quivio insights on software design, product engineering, architecture, delivery, and governed AI knowledge systems.",
  },
  "/team": {
    title: "Team | Quivio",
    description:
      "Meet the Quivio team building dependable software products, platforms, and systems for complex operations.",
  },
  "/contact": {
    title: "Contact Quivio | Start a Software Project",
    description:
      "Contact Quivio to discuss custom software development, product engineering, platform work, or Quivio Enclave KMS.",
  },
  "/thank-you": {
    title: "Thank You | Quivio",
    description: "Thank you for contacting Quivio.",
    noindex: true,
  },
};

const toCanonicalUrl = (pathname) => `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

const getText = (value) => {
  if (!value || typeof value === "string") {
    return value;
  }

  return value.en || Object.values(value)[0] || "";
};

const getMetadataForPath = (pathname) => {
  if (pageMetadata[pathname]) {
    return pageMetadata[pathname];
  }

  const blogMatch = pathname.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const post = blogPosts.find((item) => item.slug === blogMatch[1]);
    if (post) {
      return {
        title: `${getText(post.title)} | Quivio`,
        description: getText(post.summary),
        type: "article",
      };
    }
  }

  if (pathname.match(/^\/works\/[^/]+$/)) {
    return {
      title: "Selected Work | Quivio",
      description:
        "A closer look at selected Quivio software design, engineering, and digital product work.",
    };
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  };
};

const upsertMeta = (attribute, key, content) => {
  if (!content) {
    return;
  }

  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const upsertJsonLd = (metadata, canonicalUrl) => {
  let element = document.getElementById("route-json-ld");
  if (!element) {
    element = document.createElement("script");
    element.id = "route-json-ld";
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": metadata.type === "article" ? "BlogPosting" : "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: metadata.title,
    headline: metadata.title,
    description: metadata.description,
    image: DEFAULT_IMAGE,
    isPartOf: {
      "@id": `${SITE_URL}/#website`,
    },
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    inLanguage: ["en-CA", "fr-CA"],
  });
};

const Seo = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const normalizedPath = pathname === "" ? "/" : pathname;
    const metadata = getMetadataForPath(normalizedPath);
    const canonicalUrl = toCanonicalUrl(normalizedPath);
    const robots = metadata.noindex ? "noindex, nofollow" : "index, follow";

    document.title = metadata.title;
    upsertCanonical(canonicalUrl);

    upsertMeta("name", "description", metadata.description);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "application-name", "Quivio");
    upsertMeta("name", "apple-mobile-web-app-title", "Quivio");

    upsertMeta("property", "og:title", metadata.title);
    upsertMeta("property", "og:description", metadata.description);
    upsertMeta("property", "og:type", metadata.type === "article" ? "article" : "website");
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);
    upsertMeta("property", "og:image:secure_url", DEFAULT_IMAGE);
    upsertMeta("property", "og:image:alt", "Quivio logo");
    upsertMeta("property", "og:site_name", "Quivio");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", metadata.title);
    upsertMeta("name", "twitter:description", metadata.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);
    upsertMeta("name", "twitter:image:alt", "Quivio logo");

    upsertJsonLd(metadata, canonicalUrl);
  }, [pathname]);

  return null;
};

export default Seo;
